/**
 * A widget over time or over brokers, in the reader's words: its window, how
 * it steps, what its figures cover, and the order of its steps for a table.
 *
 * NOTHING HERE COMPUTES A FIGURE. A step's value, a series' move and the
 * note of what it shows are the engine's; this names the window, formats the
 * engine's ISO dates, and compares two of the engine's own strings to say
 * which way a step went (a colour, never a printed number).
 *
 * DATES ARE THE GREGORIAN ONES THE NOTE USES. The rest of the app shows a
 * Bikram Sambat date, but a windowed widget's note is the server's sentence
 * ("from Rs 200.00 on 20 Sep 2026 …"), and an axis in one calendar under a
 * sentence in the other would make the reader convert to check one against
 * the other. So the axis, the table and the period line say "19 Oct 2026",
 * as the note does.
 */
import { compareValues } from '@/features/dashboard/lib/format'
import type {
  Drawn,
  Scope,
  Series,
  TimeEvery,
  TimeSpec,
  TimeShow,
  TimeVocabulary,
  TimeWindow,
  WidgetDraft,
} from '@/features/mitra/api/dashboard'

/** The window's name where the vocabulary has not loaded (a saved widget draws without it). */
export const WINDOW_WORDS: Record<TimeWindow, string> = {
  '1W': '1 week',
  '1M': '1 month',
  '3M': '3 months',
  '6M': '6 months',
  '1Y': '1 year',
  YTD: 'This year',
  custom: 'Custom',
}

export const EVERY_WORDS: Record<TimeEvery, string> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
}

export const SHOW_WORDS: Record<TimeShow, string> = {
  line: 'Line',
  bars: 'Bars',
  table: 'Table',
}

/** What a step is called, for "20 of 21 days" under a series. */
export const STEP_NOUN: Record<TimeEvery, string> = {
  day: 'days',
  week: 'weeks',
  month: 'months',
}

/**
 * Whether a widget is drawn as a window rather than as today's figure: it has
 * a time dimension, or it is over brokers (which always has one). Such a
 * widget draws on its own card, never as a column of a holdings table.
 */
export function isWindowed(widget: Pick<Drawn, 'scope'> & { time?: TimeSpec | null | undefined }) {
  return (widget.time !== undefined && widget.time !== null) || widget.scope.kind === 'brokers'
}

/** Over brokers. */
export const isBrokers = (scope: Scope | undefined): boolean => scope?.kind === 'brokers'

/**
 * Month names as the server's note writes them ("Sep", never ICU's newer
 * en-GB "Sept"), so the axis and the sentence under it spell a date alike.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const DAY = { format: (d: Date) => `${DAY_SHORT.format(d)} ${String(d.getUTCFullYear())}` }
const DAY_SHORT = {
  format: (d: Date) => `${String(d.getUTCDate())} ${MONTHS[d.getUTCMonth()] ?? ''}`,
}
const MONTH = {
  format: (d: Date) => `${MONTHS[d.getUTCMonth()] ?? ''} ${String(d.getUTCFullYear())}`,
}

/** An ISO date read as that calendar day, wherever the device is. */
const dayOf = (iso: string): Date | null => {
  const d = new Date(`${iso}T00:00:00Z`)
  return Number.isNaN(d.getTime()) ? null : d
}

/** "19 Oct 2026", as the server's note writes a date. */
export function dateWords(iso: string): string {
  const d = dayOf(iso)
  return d ? DAY.format(d) : iso
}

/**
 * An axis tick: "21 Sep" for a day or a week (a week is named by its last
 * session, as the engine dates it), "Oct 2026" for a month.
 */
export function tickWords(iso: string, every: TimeEvery | null): string {
  const d = dayOf(iso)
  if (!d) return iso
  return every === 'month' ? MONTH.format(d) : DAY_SHORT.format(d)
}

/** "21 Sep – 19 Oct 2026", or one date where the window is one day. */
export function spanWords(from: string, to: string): string {
  if (from === to) return dateWords(to)
  const a = dayOf(from)
  const b = dayOf(to)
  if (!a || !b) return `${from} – ${to}`
  const sameYear = a.getUTCFullYear() === b.getUTCFullYear()
  return `${sameYear ? DAY_SHORT.format(a) : DAY.format(a)} – ${DAY.format(b)}`
}

/** The window's label: the engine's where it offers one, else ours. */
export function windowLabel(time: TimeSpec, vocabulary?: TimeVocabulary | null): string {
  if (time.window === 'custom' && time.from && time.to) return spanWords(time.from, time.to)
  return vocabulary?.windows.find((w) => w.key === time.window)?.label ?? WINDOW_WORDS[time.window]
}

/** "1 month · daily", or "1 month" for one figure over the whole window. */
export function timeWords(time: TimeSpec, vocabulary?: TimeVocabulary | null): string {
  const window = windowLabel(time, vocabulary)
  if (!time.every) return window
  const every =
    vocabulary?.every.find((e) => e.key === time.every)?.label ?? EVERY_WORDS[time.every]
  return `${window} · ${every.toLowerCase()}`
}

/**
 * What the figures actually cover, said once under the title: the floorsheet's
 * trading days where the widget read it (a broker table, turnover, trades),
 * else the period's sessions. A window the feed lags on covers less than its
 * name, and this is where the reader sees by how much.
 */
export function coversWords(widget: Pick<Drawn, 'period' | 'sources'>): string | null {
  const floor = widget.sources?.floorsheet ?? widget.sources?.brokerTotals ?? null
  const span = floor ?? widget.period ?? null
  if (!span) return null
  const days = span.sessions === 1 ? '1 trading day' : `${String(span.sessions)} trading days`
  return `Covers ${days}, ${spanWords(span.from, span.to)}`
}

/** The default drawing for a series: the engine's hint, else a line. */
export function showOf(widget: Pick<Drawn, 'time'>): TimeShow {
  return widget.time?.show ?? 'line'
}

/** Which way one step went from the one before it: a colour only. */
export type Way = 'up' | 'down' | 'flat' | null

export function wayOf(before: string | null | undefined, now: string | null | undefined): Way {
  if (before === null || before === undefined || now === null || now === undefined) return null
  const order = compareValues(now, before)
  return order > 0 ? 'up' : order < 0 ? 'down' : 'flat'
}

/** From a line's first figure to its last: which way it went over the window. */
export function lineWay(line: Series): Way {
  const values = line.points.map((p) => p.value).filter((v): v is string => v !== null)
  return wayOf(values[0], values[values.length - 1])
}

/** A line's last figure, the one its legend shows. */
export function lastValue(line: Series): string | null {
  for (let i = line.points.length - 1; i >= 0; i -= 1) {
    const value = line.points[i]?.value
    if (value !== null && value !== undefined) return value
  }
  return null
}

/** One row of a series table: a step's date and each line's figure at it. */
export interface StepRow {
  date: string
  from: string
  cells: { value: string | null; missing: string[]; way: Way }[]
}

/**
 * A series as table rows, newest first. Every line is stepped alike by the
 * engine, so the steps are the first line's, and each cell carries which way
 * it went from the step before (oldest-first order), for the green and red.
 */
export function stepRows(series: readonly Series[]): StepRow[] {
  const dates: { date: string; from: string }[] = []
  const seen = new Set<string>()
  for (const line of series) {
    for (const point of line.points) {
      if (!seen.has(point.date)) {
        seen.add(point.date)
        dates.push({ date: point.date, from: point.from })
      }
    }
  }
  dates.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
  const at = series.map((line) => new Map(line.points.map((p) => [p.date, p])))
  const rows = dates.map(({ date, from }, index) => ({
    date,
    from,
    cells: at.map((points) => {
      const point = points.get(date)
      const previous = index > 0 ? points.get(dates[index - 1]?.date ?? '') : undefined
      return {
        value: point?.value ?? null,
        missing: point?.missing ?? [],
        way: wayOf(previous?.value, point?.value),
      }
    }),
  }))
  return rows.reverse()
}

/** A preset made into a widget: the company put in for `"{symbol}"`, and its
 *  title named for it, as the engine does for a template. */
export function presetWidget(
  preset: WidgetDraft & { needsSymbol?: boolean | undefined },
  symbol?: string,
): WidgetDraft {
  const company = symbol?.trim().toUpperCase() ?? ''
  const scope = preset.scope
  const filled: Scope | undefined =
    scope && (scope.kind === 'symbols' || scope.kind === 'brokers') && company
      ? { ...scope, symbols: scope.symbols.map((s) => (s === '{symbol}' ? company : s)) }
      : scope
  return {
    title: preset.needsSymbol && company ? `${preset.title} · ${company}` : preset.title,
    formula: preset.formula,
    kind: preset.kind,
    unit: preset.unit,
    decimals: preset.decimals,
    ...(filled ? { scope: filled } : {}),
    ...(preset.time ? { time: preset.time } : {}),
    ...(preset.rank ? { rank: preset.rank } : {}),
    ...(preset.limit ? { limit: preset.limit } : {}),
  }
}

/** The unit a broker or history figure's own unit draws in, where it is one. */
export function widgetUnitOf(unit: string): WidgetDraft['unit'] | null {
  return unit === 'NPR' ||
    unit === 'percent' ||
    unit === 'ratio' ||
    unit === 'shares' ||
    unit === 'count'
    ? unit
    : null
}

/** A figure's name as words: `net_amount` → "Net amount". */
export function nameWords(name: string): string {
  const words = name.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}
